import { useEffect, useState } from "react";
import ToggleSwitch from "../../components/ToggleSwitch";
import FileInputImageAndPdf from "../../components/TaxesSettingsInputs/FileInputImageAndPdf";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorMessage from "@/components/ErrorMessage";
import TaxesMessage from "@/components/TaxesSettingsInputs/TaxesMessage";
import { FaDownload } from "react-icons/fa6";
import TaxNumberInput from "@/components/TaxesSettingsInputs/TaxNumberInput";
import { useTranslation } from "react-i18next";

function TaxesSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [file, setFile] = useState<File | null>(null);
  const [attachment, setAttachment] = useState<string | null>(null);
  const [taxActivated, setTaxActivated] = useState(false);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const [clientSideTaxActivated, setClientSideTaxActivated] =
    useState(taxActivated);
  const [clientSideTaxNotActivated, setClientSideTaxNotActivated] =
    useState(!taxActivated);

  const [NoTaxNumberActivated, setNoTaxNumberActivated] = useState(false);

  const [taxNumber, setTaxNumber] = useState("");
  const [taxNumberRequiredErrorMessage, setTaxNumberRequiredErrorMessage] =
    useState<string | null>(null);

  const handleNoTaxNumberActivated = () => {
    if (!NoTaxNumberActivated) {
      setNoTaxNumberActivated(true);
      setClientSideTaxActivated(false);
      setClientSideTaxNotActivated(false);
    }
    else {
      setNoTaxNumberActivated(false);
      setClientSideTaxActivated(true);
      setClientSideTaxNotActivated(false);
    }

  }

  const handleToggleClintSideTaxActivated = () => {
    setClientSideTaxActivated(!clientSideTaxActivated);
    setClientSideTaxNotActivated(clientSideTaxActivated);
    setNoTaxNumberActivated(false);
  };

  const handleToggleClintSideTaxNotActivated = () => {
    setClientSideTaxNotActivated(!clientSideTaxNotActivated);
    setClientSideTaxActivated(clientSideTaxNotActivated);
    setNoTaxNumberActivated(false);
  };

  const BASE_URL = import.meta.env.VITE_BASE_URL;

  useEffect(() => {
    if (!token) return;

    setNeedRefetch(false);
    setIsLoading(true);
    setError(null);
    setIsRequesting(false);

    const fetchTaxes = async () => {
      const url = `${BASE_URL}/api/dashboard/settings/tax-info?locale=${lang}`;

      try {
        const res = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!res.data.success) {
          const message =
            res.data.error ||
            ( t("taxSettings.fetchFailed") as string);
          setError(message);
          toast.error(message);
          setIsLoading(false);
          return;
        }

        setAttachment(res.data.attachment);
        setTaxNumber(res.data.tax || "");

        if (res.data.taxEnabled) {
          setTaxActivated(res.data.taxActivated);
          setClientSideTaxActivated(res.data.taxActivated);
          setClientSideTaxNotActivated(!res.data.taxActivated);
          setTaxEnabled(true);
          setNoTaxNumberActivated(false);
        }
        else {
          setTaxActivated(false);
          setClientSideTaxActivated(false);
          setClientSideTaxNotActivated(false);
          setTaxEnabled(false);
          setNoTaxNumberActivated(true);
        }

      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            t("taxSettings.fetchFailed")
          setError(message);
          toast.error(message);
        } else {
          const message = t("taxSettings.unexpectedError") as string;
          setError(message);
          toast.error(message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaxes();
  }, [BASE_URL, lang, needRefetch, token, t]);

  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  const handleSendTaxUpdateRequest = async () => {
    // if (!file) return;

    if (!taxNumber.length) {
      setTaxNumberRequiredErrorMessage(
        t("taxSettings.taxNumberRequired") as string
      );
      return;
    }
    else {
      setTaxNumberRequiredErrorMessage(null);

    }

    const LoadingToast = toast.loading(
      t("taxSettings.updating") as string,
    );
    setIsRequesting(true);
    const url = `${BASE_URL}/api/dashboard/settings/tax-info?locale=${lang}`;

    const body = NoTaxNumberActivated ? { taxEnabled: false } : {
      attachment: file,
      taxActivated: clientSideTaxActivated,
      taxEnabled: !NoTaxNumberActivated,
      tax: taxNumber || undefined,
    }

    try {
      const res = await axios.put(
        url,
        body
        , {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!res.data.success) {
        const message =
          res.data.error ||
          (t("taxSettings.updateFailed") as string);
        toast.error(message);
        return;
      }

      toast.success(
        t("taxSettings.updateSuccess") as string,
      );
      setNeedRefetch(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          t("taxSettings.updateFailed")
        toast.error(message);
      } else {
        toast.error(
          t("taxSettings.unexpectedError") as string,
        );
      }
    } finally {
      toast.dismiss(LoadingToast);
      setIsRequesting(false);
    }
  };

  if (isLoading)
    return (
      <Skeleton className="mt-10 h-[550px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="self-start text-2xl font-semibold">
        {t("taxSettings.title")}
      </h1>

      <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6 pe-7">
        <TaxesMessage taxEnabled={taxEnabled} taxActivated={taxActivated} />

        <div className="flex items-center gap-3">
          <span className="text-xl">
            {t("taxSettings.vat")}
          </span>
        </div>

        {/* toggle 1 */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-semibold capitalize text-gray-700">
            {t("taxSettings.add15Percent")}
          </h1>

          <ToggleSwitch
            size="small"
            checked={clientSideTaxActivated}
            onChange={handleToggleClintSideTaxActivated}
            disabled={isRequesting || isLoading}
          />
        </div>

        {/* toggle 2 */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-semibold capitalize text-gray-700">
            {t("taxSettings.pricesIncludeVat")}

          </h1>
          <ToggleSwitch
            size="small"
            checked={clientSideTaxNotActivated}
            onChange={handleToggleClintSideTaxNotActivated}
            disabled={isRequesting || isLoading}
          />
        </div>

        {/* toggle 3 */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-semibold capitalize text-gray-700">
            {t("taxSettings.noTaxNumber")}

          </h1>
          <ToggleSwitch
            size="small"
            checked={NoTaxNumberActivated}
            onChange={handleNoTaxNumberActivated}
            disabled={isRequesting || isLoading}
          />
        </div>

        <div className="flex w-fit flex-col gap-3">
          {/* Tax numebr Input */}
          <TaxNumberInput
            setTaxNumber={setTaxNumber}
            taxNumber={taxNumber}
            taxRequiredErrorMessage={taxNumberRequiredErrorMessage}
            setTaxNumberRequiredErrorMessage={setTaxNumberRequiredErrorMessage}
            disabled={NoTaxNumberActivated || isRequesting || isLoading}
          />

          {/* file input */}
          <div className="space-y-2">
            <label className="font-semibold">
              {t("taxSettings.taxCertificate")}
            </label>
            <FileInputImageAndPdf
              isApiAttachment={attachment ? true : false}
              onFileChange={handleFileChange}
              disabled={NoTaxNumberActivated || isRequesting || isLoading}
            />
          </div>
        </div>

        {/* Download Previus Attachment  */}
        {attachment && (
          <div className={`flex w-full items-center gap-2 ${NoTaxNumberActivated && "invisible"}`}>
            <a
              href={attachment}
              target="_blank"
              rel="noreferrer"
              download={true}
              className="flex items-center gap-2 rounded-md bg-sidebar px-3 py-1 text-sm text-skyblue"
            >
              {t("taxSettings.downloadPreviousAttachment")}
              <FaDownload className="text-base" />
            </a>
          </div>
        )}

        <button
          disabled={isRequesting || isLoading}
          onClick={handleSendTaxUpdateRequest}
          className="mt-2 self-end rounded-md bg-sidebar px-5 py-2 text-base font-medium text-skyblue disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 md:mt-0"
        >
          {t("taxSettings.save")}
        </button>
      </div>
    </div>
  );
}

export default TaxesSettings;
